import { CheckCircle, Info } from "lucide-react";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import type { AccountConfig } from "@/lib/types/client";
import { cn } from "@/lib/utils";

/** Port von isQuizStepValid() aus src/components/wizard/steps/accountConfig.js. */
export function isQuizStepValid(idx: number, ac: AccountConfig): boolean {
  switch (idx) {
    case 0:
      if (!ac.rechnungssystem_satisfactory) return false;
      if (ac.rechnungssystem_satisfactory === "Ja") {
        if (!ac.rechnungssystem_name) return false;
        if (ac.rechnungssystem_name === "Andere" && !ac.rechnungssystem_other?.trim()) return false;
        if (!ac.rechnungssystem_interface_needed) return false;
      } else if (!ac.rechnungssystem_wants_overview) {
        return false;
      }
      return true;
    case 1:
      return !!ac.data_import_external;
    case 2:
      return !!ac.order_number_type;
    case 3:
      if (!ac.vehicle_required_on_finish) return false;
      if (ac.vehicle_required_on_finish === "Ja") return !!ac.vehicle_fixed_allocation;
      return true;
    case 4:
      return !!ac.gps_track_recording && !!ac.gps_geotab_obd;
    case 5:
      if (!ac.send_receipts_to_customers) return false;
      if (ac.send_receipts_to_customers === "Ja") {
        if (!ac.receipt_cc_self) return false;
        if (ac.receipt_cc_self === "Ja" && !ac.receipt_cc_email?.trim()) return false;
      }
      if (!ac.receipt_auto_copy) return false;
      if (ac.receipt_auto_copy === "Ja" && !ac.receipt_auto_copy_email?.trim()) return false;
      return true;
    case 6:
      return !!ac.upload_docs_images;
    case 7:
      return !!ac.use_vacation_planner && !!ac.use_mobile_time_tracking;
    case 8:
      return !!ac.use_dispoboard && !!ac.show_resources_on_map;
    case 9:
      return !!ac.scale_integration_needed && !!ac.gate_control_needed;
    default:
      return true;
  }
}

export const QUIZ_CATEGORY_TITLES = [
  "1. Rechnungssystem & Schnittstellen",
  "2. Datenimport & Schnittstellen",
  "3. Auftragsnummern",
  "4. Fahrzeuge & Fahrzeugzuordnung",
  "5. Fahrzeugdaten & Ortung",
  "6. Belege & Dokumente",
  "7. Dokumente & Bilder",
  "8. Urlaubsplanung & Zeiterfassung",
  "9. Disposition & Ressourcen",
  "10. Weitere Schnittstellen & Sonderfunktionen"
];

/** Port der summaryText-Fallunterscheidung aus renderSummaryAccordion(). */
export function summarizeQuizAnswer(idx: number, ac: AccountConfig): string {
  switch (idx) {
    case 0:
      if (ac.rechnungssystem_satisfactory === "Ja") {
        const name = ac.rechnungssystem_name === "Andere" ? ac.rechnungssystem_other : ac.rechnungssystem_name;
        return `Nutzt Rechnungssystem: ${name || "-"} (Schnittstelle: ${ac.rechnungssystem_interface_needed || "-"})`;
      }
      return `Kein Rechnungssystem (Überblick erwünscht: ${ac.rechnungssystem_wants_overview || "-"})`;
    case 1:
      return `Datenimport: ${ac.data_import_external || "-"}`;
    case 2:
      return `Auftragsnummern: ${ac.order_number_type || "-"}`;
    case 3:
      if (ac.vehicle_required_on_finish === "Ja") {
        return `Fahrzeugpflicht beim Abschluss: Ja (Feste Zuordnung: ${ac.vehicle_fixed_allocation || "-"})`;
      }
      return "Fahrzeugpflicht beim Abschluss: Nein (Standard)";
    case 4: {
      const fahrspur = ac.gps_track_recording === "Ja" ? "Ja (empfohlen)" : ac.gps_track_recording;
      return `Fahrspur: ${fahrspur || "-"} / Geotab OBD-2: ${ac.gps_geotab_obd || "-"}`;
    }
    case 5:
      if (ac.send_receipts_to_customers === "Ja") {
        const cc = ac.receipt_cc_self === "Ja" ? `CC: ${ac.receipt_cc_email || "-"}` : "Kein CC";
        const copy = ac.receipt_auto_copy === "Ja" ? `Kopie: ${ac.receipt_auto_copy_email || "-"}` : "Keine Kopie";
        return `Belege senden: Ja (${cc} / ${copy})`;
      }
      return "Belege senden: Nein";
    case 6:
      return `Bilder & Doks hochladen: ${ac.upload_docs_images || "-"}`;
    case 7:
      return `Urlaubsplaner: ${ac.use_vacation_planner || "-"} / Zeiterfassung: ${ac.use_mobile_time_tracking || "-"}`;
    case 8:
      return `Dispoboard: ${ac.use_dispoboard || "-"} / Ressourcen auf Karte: ${ac.show_resources_on_map || "-"}`;
    case 9:
      return `Waagenanbindung: ${ac.scale_integration_needed || "-"} / Hoftore öffnen: ${ac.gate_control_needed || "-"}`;
    default:
      return "";
  }
}

type UpdateAnswer = (field: keyof AccountConfig, value: string) => void;

function OptionCards({
  field,
  legend,
  helper,
  choices,
  value,
  isLocked,
  stacked,
  onSelect
}: {
  field: string;
  legend: string;
  helper?: string;
  choices: Array<{ value: string; label: string }>;
  value: string | undefined;
  isLocked: boolean;
  stacked?: boolean;
  onSelect: (value: string) => void;
}) {
  return (
    <fieldset className="mb-6">
      <legend className="mb-2 text-base font-semibold">{legend}</legend>
      {helper ? <p className="-mt-1 mb-2 text-xs text-muted-foreground">{helper}</p> : null}
      <div className={cn("grid gap-3", stacked ? "grid-cols-1" : "grid-cols-[repeat(auto-fit,minmax(200px,1fr))]")}>
        {choices.map((choice) => {
          const isSelected = value === choice.value;
          const inputId = `${field}-${choice.value}`;
          return (
            <label
              key={choice.value}
              htmlFor={inputId}
              className={cn(
                "flex items-center justify-between gap-3 rounded-md border-2 p-4 transition",
                isLocked ? "cursor-default" : "cursor-pointer",
                isSelected ? "border-primary bg-primary-fade shadow-sm" : "border-border bg-card"
              )}
            >
              <span className="text-sm font-semibold">{choice.label}</span>
              <input
                type="radio"
                id={inputId}
                name={field}
                className="sr-only"
                checked={isSelected}
                disabled={isLocked}
                onChange={() => onSelect(choice.value)}
              />
              {isSelected ? (
                <CheckCircle className="size-4 shrink-0 text-primary" aria-hidden="true" />
              ) : (
                <span className="size-4 shrink-0 rounded-full border border-border" aria-hidden="true" />
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function CostWarning({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-6 flex items-start gap-2 rounded-sm border border-warning bg-warning-fade p-3 text-sm">
      <Info className="mt-0.5 size-4.5 shrink-0 text-warning-dark" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

/** Port von renderQuizQuestionCardContent() aus src/components/wizard/steps/accountConfig.js. */
export function QuizQuestionFields({ idx, ac, isLocked, onUpdate }: { idx: number; ac: AccountConfig; isLocked: boolean; onUpdate: UpdateAnswer }) {
  switch (idx) {
    case 0:
      return (
        <div>
          <OptionCards
            field="rechnungssystem_satisfactory"
            legend="Nutzt du ein zufriedenstellendes Rechnungssystem?"
            choices={[
              { value: "Ja", label: "Ja" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.rechnungssystem_satisfactory}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("rechnungssystem_satisfactory", value)}
          />

          {ac.rechnungssystem_satisfactory === "Ja" ? (
            <div className="mb-2 border-l-2 border-l-primary pl-4">
              <OptionCards
                field="rechnungssystem_name"
                legend="Welches Rechnungssystem nutzt du?"
                choices={[
                  { value: "Sevdesk", label: "Sevdesk" },
                  { value: "Easybill", label: "Easybill" },
                  { value: "Lexware Office", label: "Lexware Office" },
                  { value: "Andere", label: "Andere" }
                ]}
                value={ac.rechnungssystem_name}
                isLocked={isLocked}
                onSelect={(value) => onUpdate("rechnungssystem_name", value)}
              />

              {ac.rechnungssystem_name === "Andere" ? (
                <Field className="mb-6">
                  <FieldLabel htmlFor="rechnungssystem_other">Welches andere Rechnungssystem nutzt du?</FieldLabel>
                  <Input
                    id="rechnungssystem_other"
                    value={ac.rechnungssystem_other || ""}
                    disabled={isLocked}
                    required
                    placeholder="z. B. SAP, Microsoft Dynamics"
                    onChange={(event) => onUpdate("rechnungssystem_other", event.target.value)}
                  />
                </Field>
              ) : null}

              <OptionCards
                field="rechnungssystem_interface_needed"
                legend="Benötigst du eine Schnittstelle für eine bequeme und einwandfreie Datenübertragung in dein Rechnungssystem?"
                choices={[
                  { value: "Ja", label: "Ja" },
                  { value: "Nein", label: "Nein" }
                ]}
                value={ac.rechnungssystem_interface_needed}
                isLocked={isLocked}
                onSelect={(value) => onUpdate("rechnungssystem_interface_needed", value)}
              />

              {ac.rechnungssystem_interface_needed === "Ja" ? (
                <CostWarning>
                  {ac.rechnungssystem_name === "Andere"
                    ? "Eine Schnittstelle zu einem anderen Rechnungssystem muss mit cleverfleet abgestimmt werden und kann in der Entwicklung weitere Kosten verursachen."
                    : "Die Schnittstelle verursacht zusätzliche Kosten, dafür, dass eine einwandfreie Datenübertragung stattfinden kann."}
                </CostWarning>
              ) : null}
            </div>
          ) : null}

          {ac.rechnungssystem_satisfactory === "Nein" ? (
            <div className="border-l-2 border-l-primary pl-4">
              <OptionCards
                field="rechnungssystem_wants_overview"
                legend="Möchtest du einen Überblick zu einem kompatiblen und modernen Rechnungssystem erhalten?"
                stacked
                choices={[
                  { value: "Ja, das könnte für mich interessant sein", label: "Ja, das könnte für mich interessant sein" },
                  { value: "Nein, benötige ich nicht", label: "Nein, benötige ich nicht" }
                ]}
                value={ac.rechnungssystem_wants_overview}
                isLocked={isLocked}
                onSelect={(value) => onUpdate("rechnungssystem_wants_overview", value)}
              />
            </div>
          ) : null}
        </div>
      );
    case 1:
      return (
        <div>
          <OptionCards
            field="data_import_external"
            legend="Möchtest du regelmäßig Daten aus einem externen System (z. B. ERP-System) importieren?"
            stacked
            choices={[
              { value: "Ja, ich möchte die Funktion über einen CSV-Import nutzen", label: "Ja, ich möchte die Funktion über einen CSV-Import nutzen" },
              { value: "Nein", label: "Nein" },
              { value: "Eine separate Schnittstelle soll geschaffen werden", label: "Eine separate Schnittstelle soll geschaffen werden" }
            ]}
            value={ac.data_import_external}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("data_import_external", value)}
          />
          {ac.data_import_external === "Eine separate Schnittstelle soll geschaffen werden" ? (
            <CostWarning>
              Eine separate Schnittstelle verursacht zusätzliche Kosten, da diese individuell eingerichtet werden muss und keine standardisierte
              API von uns genutzt werden kann.
            </CostWarning>
          ) : null}
        </div>
      );
    case 2:
      return (
        <OptionCards
          field="order_number_type"
          legend="Möchtest du eine eigene oder automatisch erstellte fortlaufende Auftragsnummer verwenden?"
          stacked
          choices={[
            { value: "Eigene Auftragsnummern verwenden", label: "Eigene Auftragsnummern verwenden" },
            { value: "Automatisch fortlaufende Auftragsnummern durch cleverfleet erstellen lassen", label: "Automatisch fortlaufende Auftragsnummern durch cleverfleet erstellen lassen" }
          ]}
          value={ac.order_number_type}
          isLocked={isLocked}
          onSelect={(value) => onUpdate("order_number_type", value)}
        />
      );
    case 3:
      return (
        <div>
          <OptionCards
            field="vehicle_required_on_finish"
            legend="Soll um einen Auftrag abschließen zu können immer diesem ein Fahrzeug zugeordnet sein müssen?"
            choices={[
              { value: "Ja", label: "Ja" },
              { value: "Nein", label: "Nein (Standard)" }
            ]}
            value={ac.vehicle_required_on_finish}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("vehicle_required_on_finish", value)}
          />
          {ac.vehicle_required_on_finish === "Ja" ? (
            <div className="border-l-2 border-l-primary pl-4">
              <OptionCards
                field="vehicle_fixed_allocation"
                legend="Haben deine Fahrzeuge eine feste Fahrzeugzuteilung, die nicht verändert werden kann?"
                choices={[
                  { value: "Ja", label: "Ja" },
                  { value: "Nein", label: "Nein (empfohlen)" }
                ]}
                value={ac.vehicle_fixed_allocation}
                isLocked={isLocked}
                onSelect={(value) => onUpdate("vehicle_fixed_allocation", value)}
              />
            </div>
          ) : null}
        </div>
      );
    case 4:
      return (
        <div>
          <OptionCards
            field="gps_track_recording"
            legend="Möchtest du eine Fahrspuraufzeichnung über die Tablets aktivieren?"
            choices={[
              { value: "Ja", label: "Ja (empfohlen)" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.gps_track_recording}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("gps_track_recording", value)}
          />
          <OptionCards
            field="gps_geotab_obd"
            legend="Möchtest du neben der Tablet-Ortung zusätzlich GEOTAB OBD-2-Stecker in deinen Fahrzeugen integrieren und nutzen?"
            helper="Damit können weitere Fahrzeugdaten aufgezeichnet und in cleverfleet dargestellt werden."
            choices={[
              { value: "Ja", label: "Ja" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.gps_geotab_obd}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("gps_geotab_obd", value)}
          />
          {ac.gps_geotab_obd === "Ja" ? (
            <CostWarning>Die GEOTAB-Anbindung verursacht zusätzliche Kosten und muss individuell mit cleverfleet abgestimmt werden.</CostWarning>
          ) : null}
        </div>
      );
    case 5:
      return (
        <div>
          <OptionCards
            field="send_receipts_to_customers"
            legend="Möchtest du Kunden direkt aus cleverfleet heraus Belege zusenden können?"
            choices={[
              { value: "Ja", label: "Ja (Standard)" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.send_receipts_to_customers}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("send_receipts_to_customers", value)}
          />
          {ac.send_receipts_to_customers === "Ja" ? (
            <div className="border-l-2 border-l-primary pl-4">
              <OptionCards
                field="receipt_cc_self"
                legend="Möchtest du beim Versand eines Belegs automatisch eine Kopie per CC erhalten?"
                choices={[
                  { value: "Ja", label: "Ja" },
                  { value: "Nein", label: "Nein" }
                ]}
                value={ac.receipt_cc_self}
                isLocked={isLocked}
                onSelect={(value) => onUpdate("receipt_cc_self", value)}
              />
              {ac.receipt_cc_self === "Ja" ? (
                <Field className="mb-6">
                  <FieldLabel htmlFor="receipt_cc_email">Welche E-Mail-Adresse soll automatisch als CC verwendet werden?</FieldLabel>
                  <Input
                    id="receipt_cc_email"
                    type="email"
                    value={ac.receipt_cc_email || ""}
                    disabled={isLocked}
                    required
                    placeholder="z. B. buchhaltung@firma.de"
                    onChange={(event) => onUpdate("receipt_cc_email", event.target.value)}
                  />
                </Field>
              ) : null}
            </div>
          ) : null}

          <OptionCards
            field="receipt_auto_copy"
            legend="Soll bei jedem fertiggestellten Auftrag automatisch ein Durchschlag an eine E-Mail-Adresse versendet werden?"
            choices={[
              { value: "Ja", label: "Ja" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.receipt_auto_copy}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("receipt_auto_copy", value)}
          />
          {ac.receipt_auto_copy === "Ja" ? (
            <div className="border-l-2 border-l-primary pl-4">
              <Field className="mb-6">
                <FieldLabel htmlFor="receipt_auto_copy_email">Welche E-Mail-Adresse soll den Durchschlag erhalten?</FieldLabel>
                <Input
                  id="receipt_auto_copy_email"
                  type="email"
                  value={ac.receipt_auto_copy_email || ""}
                  disabled={isLocked}
                  required
                  placeholder="z. B. archiv@firma.de"
                  onChange={(event) => onUpdate("receipt_auto_copy_email", event.target.value)}
                />
              </Field>
            </div>
          ) : null}
        </div>
      );
    case 6:
      return (
        <OptionCards
          field="upload_docs_images"
          legend="Möchtest du Dokumente und Bilder zu Aufträgen hochladen können?"
          choices={[
            { value: "Ja", label: "Ja (empfohlen)" },
            { value: "Nein", label: "Nein" }
          ]}
          value={ac.upload_docs_images}
          isLocked={isLocked}
          onSelect={(value) => onUpdate("upload_docs_images", value)}
        />
      );
    case 7:
      return (
        <div>
          <OptionCards
            field="use_vacation_planner"
            legend="Möchtest du einen Urlaubsplaner in cleverfleet nutzen?"
            stacked
            choices={[
              { value: "Ja, vollständig, auch mit Genehmigungsverfahren", label: "Ja, vollständig, auch mit Genehmigungsverfahren" },
              { value: "Ja, mit einfacher Eintragung durch den Disponenten", label: "Ja, mit einfacher Eintragung durch den Disponenten" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.use_vacation_planner}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("use_vacation_planner", value)}
          />
          <OptionCards
            field="use_mobile_time_tracking"
            legend="Möchtest du die mobile Zeiterfassung in cleverfleet nutzen?"
            choices={[
              { value: "Ja", label: "Ja" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.use_mobile_time_tracking}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("use_mobile_time_tracking", value)}
          />
        </div>
      );
    case 8:
      return (
        <div>
          <OptionCards
            field="use_dispoboard"
            legend="Möchtest du unser cleverfleet Dispoboard nutzen?"
            choices={[
              { value: "Ja", label: "Ja (empfohlen)" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.use_dispoboard}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("use_dispoboard", value)}
          />
          <OptionCards
            field="show_resources_on_map"
            legend="Hast du Ressourcen, die du auf der Dispokarte gerne angezeigt bekommen möchtest, wenn du diese einsetzt?"
            choices={[
              { value: "Ja", label: "Ja" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.show_resources_on_map}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("show_resources_on_map", value)}
          />
        </div>
      );
    case 9:
      return (
        <div>
          <OptionCards
            field="scale_integration_needed"
            legend="Benötigst du eine Waagenanbindung?"
            choices={[
              { value: "Ja", label: "Ja" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.scale_integration_needed}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("scale_integration_needed", value)}
          />
          <OptionCards
            field="gate_control_needed"
            legend="Möchtest du deine Hof- & Werkstore über cleverfleet öffnen und schließen können?"
            choices={[
              { value: "Ja", label: "Ja" },
              { value: "Nein", label: "Nein" }
            ]}
            value={ac.gate_control_needed}
            isLocked={isLocked}
            onSelect={(value) => onUpdate("gate_control_needed", value)}
          />
          <CostWarning>Sowohl die Waagenanbindung als auch die Werkstorsteuerung verursachen zusätzliche Kosten und müssen individuell mit cleverfleet abgestimmt werden.</CostWarning>
        </div>
      );
    default:
      return null;
  }
}
